import * as React from 'react';
import axios from 'axios';
import { BASE_URL } from '../../config/environmentConfig';
import { sendSentryMessage } from '../utils/sentry-helper';
import { INotificationsData } from '../components/header/HeaderNotification';
import { GET } from './store-helper';

enum notificationsActionTypes {
  SET_NOTIFICATIONS_LOADING = 'SET_NOTIFICATIONS_LOADING',
  FETCH_NOTIFICATIONS_SUCCESS = 'FETCH_NOTIFICATIONS_SUCCESS',
  FETCH_NOTIFICATIONS_FAIL = 'FETCH_NOTIFICATIONS_FAIL',
  UPDATE_NOTIFICATIONS_SUCCESS = 'UPDATE_NOTIFICATIONS_SUCCESS',
  UPDATE_NOTIFICATIONS_FAIL = 'UPDATE_NOTIFICATIONS_FAIL',
}

interface IAction {
  type: notificationsActionTypes;
  payload: INotificationsData;
}

interface IState {
  notificationsData: INotificationsData | null;
  isNotificationLoading: boolean;
}

type NotificationsProviderProps = { children: React.ReactElement }

const NotificationsStateContext = React.createContext<
  {notificationContextState: IState; dispatch: React.Dispatch<IAction>} | undefined
  >(undefined);

function notificationsReducer(state: IState, action: IAction): IState {
  switch (action.type) {
    case notificationsActionTypes.SET_NOTIFICATIONS_LOADING: {
      return { ...state, isNotificationLoading: true };
    }
    case notificationsActionTypes.FETCH_NOTIFICATIONS_SUCCESS: {
      return { ...state, notificationsData: action.payload, isNotificationLoading: false };
    }
    case notificationsActionTypes.UPDATE_NOTIFICATIONS_SUCCESS: {
      return { ...state, isNotificationLoading: false };
    }
    case notificationsActionTypes.UPDATE_NOTIFICATIONS_FAIL: {
      return { ...state, isNotificationLoading: false };
    }
    default: {
      return state;
    }
  }
}

function NotificationsProvider({ children }: NotificationsProviderProps): JSX.Element {
  const [notificationContextState, dispatch] = React.useReducer(notificationsReducer, { notificationsData: null, isNotificationLoading: false });
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context
  const value = { notificationContextState, dispatch };
  return (
    <NotificationsStateContext.Provider value={value}>
      {children}
    </NotificationsStateContext.Provider>
  );
}

function useNotificationContext(): { notificationContextState: IState, dispatch: React.Dispatch<IAction> } {
  const context = React.useContext(NotificationsStateContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationsProvider');
  }
  return context;
}

export const fetchNotifications = async (dispatch): Promise<void> => {
  dispatch({ type: notificationsActionTypes.SET_NOTIFICATIONS_LOADING });
  try {

    const notifications = await GET({ pathname: `${BASE_URL.NOTIFICATION}` });

    dispatch({ type: notificationsActionTypes.FETCH_NOTIFICATIONS_SUCCESS, payload: notifications });
  } catch (e) {
    sendSentryMessage({ name: `GET Fetch notifications error`, context: e });
    dispatch({ type: notificationsActionTypes.FETCH_NOTIFICATIONS_FAIL, error: e });
  }
};

export async function setNotificationsAsRead(dispatch, id): Promise<void> {
  dispatch({ type: notificationsActionTypes.SET_NOTIFICATIONS_LOADING });
  try {
    let queryParam = '';

    if (id) {
      queryParam = `&id=${id}`;
    }
    await axios.post(`${BASE_URL.NOTIFICATION}?action=2${queryParam}`);
    dispatch({ type: notificationsActionTypes.UPDATE_NOTIFICATIONS_SUCCESS });
    fetchNotifications(dispatch);
  } catch (e) {
    sendSentryMessage({ name: `POST Set notification as read error`, context: e });
    dispatch({ type: notificationsActionTypes.UPDATE_NOTIFICATIONS_FAIL, error: e });
  }
}

export async function setNotificationsAsSean(dispatch): Promise<void> {
  dispatch({ type: notificationsActionTypes.SET_NOTIFICATIONS_LOADING });
  try {
    await axios.post(`${BASE_URL.NOTIFICATION}`);
    dispatch({ type: notificationsActionTypes.UPDATE_NOTIFICATIONS_SUCCESS });
    fetchNotifications(dispatch);
  } catch (e) {
    sendSentryMessage({ name: `POST Set notification as sean error`, context: e });
    dispatch({ type: notificationsActionTypes.UPDATE_NOTIFICATIONS_FAIL, error: e });
  }
}

export { NotificationsProvider, useNotificationContext };
